import type {
    ComputingCodeFormat,
    ComputingCodeMultiFile,
} from "#content-library/types/types.ts";

/**
 * jsonStringToComputingCodeMultiFile converts the code string of a computing
 * program into a JavaScript object in the ComputingCodeMultiFile format. Will
 * throw an exception if the code string is invalid JSON or has the wrong
 * structure.
 */
export const jsonStringToComputingCodeMultiFile = (
    code: string,
    codeFormat: ComputingCodeFormat,
): ComputingCodeMultiFile => {
    switch (codeFormat) {
        case "MULTI_FILE_JSON":
            return deserializeMultiFileJson(code);
        case "SINGLE_FILE":
            throw new Error("Python program has SINGLE_FILE format");
        default:
            const _: never = codeFormat;
            throw new TypeError("Unknown codeFormat value in Python program");
    }
};

export const computingCodeMultiFileToKhanmigoString = (
    code: ComputingCodeMultiFile,
): string => {
    // ~8k tokens for gpt-4 * ~3 characters = 24k characters
    // Max code size is set to half of that to account
    // for the rest of the prompt and the conversation.
    const maxCodeSize = 12000;

    return JSON.stringify(code).substring(0, maxCodeSize);
};

/**
 * deserializeMultiFileJson takes a MULTI_FILE_JSON code string, validates it,
 * and converts it to a JavaScript object. Will throw an exception if the code
 * string is invalid JSON or has the wrong structure.
 */
const deserializeMultiFileJson = (code: string): ComputingCodeMultiFile => {
    // JSON.parse() will throw a SyntaxError if it fails to parse the JSON.
    const codeObject = JSON.parse(code);
    if (
        typeof codeObject !== "object" ||
        codeObject === null ||
        Array.isArray(codeObject)
    ) {
        throw new Error("Could not parse code into object");
    }

    const files = codeObject["files"];
    if (!Array.isArray(files)) {
        throw new Error("Code does not contain a files array");
    }

    return {
        files: files.map((file) => {
            if (
                typeof file !== "object" ||
                file === null ||
                Array.isArray(file) ||
                typeof file["code"] !== "string" ||
                typeof file["filename"] !== "string" ||
                typeof file["language"] !== "string"
            ) {
                throw new Error("Code contains invalid object in files array.");
            }

            return {
                code: file["code"],
                filename: file["filename"],
                language: file["language"],
            };
        }),
    };
};
